import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AudioPin } from '../lib/supabase';
import { AlertCircle } from 'lucide-react';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || '';

if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

type Zone = {
  id: string;
  name: string;
  coordinates: any;
  shape_type: 'circle' | 'polygon';
  latitude: number;
  longitude: number;
  radius?: number;
  user_id: string;
  playlist_id: string | null;
  is_active: boolean;
};

type MapProps = {
  audioPins?: AudioPin[];
  zones?: Zone[];
  onPinClick: (pin: AudioPin) => void;
  onMapClick: (lat: number, lng: number) => void;
  onZoneClick?: (zone: Zone) => void;
  onEditPin?: (pin: AudioPin) => void;
  onDeletePin?: (pin: AudioPin) => void;
  center?: [number, number];
  zoom?: number;
  searchQuery?: string;
  currentUserId?: string;
  onMapMove?: (center: [number, number], zoom: number) => void;
  searchLocation?: { lat: number; lng: number; name: string } | null;
  isDrawingZone?: boolean;
  drawnPolygon?: [number, number][] | null;
  onPolygonUpdate?: (polygon: [number, number][]) => void;
};

export function MapView({
  audioPins = [],
  zones = [],
  onPinClick,
  onMapClick,
  onZoneClick,
  onEditPin,
  onDeletePin,
  center = [0, 0],
  zoom = 2,
  searchQuery,
  currentUserId,
  onMapMove,
  searchLocation,
  isDrawingZone = false,
  drawnPolygon = null,
  onPolygonUpdate
}: MapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const zoneMarkers = useRef<mapboxgl.Marker[]>([]);
  const searchMarker = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isUserMovingMap = useRef(false);
  const prevCenter = useRef(center);
  const prevZoom = useRef(zoom);
  const isDrawingZoneRef = useRef(isDrawingZone);
  const drawnPolygonRef = useRef(drawnPolygon);
  const onPolygonUpdateRef = useRef(onPolygonUpdate);

  // Keep refs in sync
  useEffect(() => {
    isDrawingZoneRef.current = isDrawingZone;
  }, [isDrawingZone]);

  useEffect(() => {
    drawnPolygonRef.current = drawnPolygon;
  }, [drawnPolygon]);

  useEffect(() => {
    onPolygonUpdateRef.current = onPolygonUpdate;
  }, [onPolygonUpdate]);

  // Initialize map
  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setError('Mapbox access token is missing. Please add VITE_MAPBOX_ACCESS_TOKEN to your .env file.');
      return;
    }

    if (!mapContainer.current || map.current) return;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: center,
        zoom: zoom,
      });

      // Add navigation controls (zoom, compass)
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add geolocation control (my location)
      map.current.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true
          },
          trackUserLocation: true,
          showUserHeading: true
        }),
        'top-right'
      );

      map.current.on('load', () => {
        setMapLoaded(true);
      });

      map.current.on('click', (e) => {
        console.log('Map clicked, isDrawingZone:', isDrawingZoneRef.current);

        if (isDrawingZoneRef.current && onPolygonUpdateRef.current) {
          console.log('Drawing mode active, adding point');
          const newPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          const currentPolygon = drawnPolygonRef.current || [];

          if (currentPolygon.length > 0) {
            const firstPoint = currentPolygon[0];
            const distance = Math.sqrt(
              Math.pow(firstPoint[0] - newPoint[0], 2) +
              Math.pow(firstPoint[1] - newPoint[1], 2)
            );

            if (distance < 0.001) {
              console.log('Closing polygon');
              onPolygonUpdateRef.current([...currentPolygon, firstPoint]);
              return;
            }
          }

          onPolygonUpdateRef.current([...currentPolygon, newPoint]);
        } else {
          // Check if clicking on a zone layer
          const features = map.current?.queryRenderedFeatures(e.point, {
            layers: ['zones-fill']
          });

          if (features && features.length > 0) {
            console.log('Clicked on zone, not opening create modal');
            return;
          }

          console.log('Normal mode, calling onMapClick');
          onMapClick(e.lngLat.lat, e.lngLat.lng);
        }
      });

      // Track map position changes
      map.current.on('movestart', () => {
        isUserMovingMap.current = true;
      });

      map.current.on('moveend', () => {
        if (map.current && onMapMove) {
          const center = map.current.getCenter();
          const zoom = map.current.getZoom();
          const newCenter: [number, number] = [center.lng, center.lat];
          const newZoom = zoom;

          prevCenter.current = newCenter;
          prevZoom.current = newZoom;
          onMapMove(newCenter, newZoom);
        }

        setTimeout(() => {
          isUserMovingMap.current = false;
        }, 100);
      });
    } catch (err: any) {
      console.error('Map initialization error:', err);
      setError(err.message || 'Failed to initialize map');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update map center and zoom when props change externally (not from user movement)
  useEffect(() => {
    if (!map.current || !mapLoaded || isUserMovingMap.current) return;

    // Check if center or zoom actually changed (not just reference change)
    const centerChanged =
      prevCenter.current[0] !== center[0] ||
      prevCenter.current[1] !== center[1];
    const zoomChanged = Math.abs(prevZoom.current - zoom) > 0.01;

    if (centerChanged || zoomChanged) {
      prevCenter.current = center;
      prevZoom.current = zoom;

      map.current.flyTo({
        center: center,
        zoom: zoom,
        duration: 1500,
        essential: true
      });
    }
  }, [center, zoom, mapLoaded]);

  // Handle edit pin events
  useEffect(() => {
    if (!onEditPin) return;

    const handleEditPin = (e: Event) => {
      const customEvent = e as CustomEvent;
      const pinId = customEvent.detail;
      const pin = audioPins.find(p => p.id === pinId);
      if (pin) {
        onEditPin(pin);
      }
    };

    window.addEventListener('edit-pin', handleEditPin);
    return () => window.removeEventListener('edit-pin', handleEditPin);
  }, [audioPins, onEditPin]);

  // Handle delete pin events
  useEffect(() => {
    if (!onDeletePin) return;

    const handleDeletePin = (e: Event) => {
      const customEvent = e as CustomEvent;
      const pinId = customEvent.detail;
      const pin = audioPins.find(p => p.id === pinId);
      if (pin) {
        onDeletePin(pin);
      }
    };

    window.addEventListener('delete-pin', handleDeletePin);
    return () => window.removeEventListener('delete-pin', handleDeletePin);
  }, [audioPins, onDeletePin]);

  // Update cursor style based on drawing mode
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (isDrawingZone) {
      map.current.getCanvas().style.cursor = 'crosshair';
    } else {
      map.current.getCanvas().style.cursor = '';
    }
  }, [isDrawingZone, mapLoaded]);

  // Handle polygon drawing visualization
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clean up existing layers and sources
    if (map.current.getLayer('drawing-polygon-fill')) {
      map.current.removeLayer('drawing-polygon-fill');
    }
    if (map.current.getLayer('drawing-polygon-line')) {
      map.current.removeLayer('drawing-polygon-line');
    }
    if (map.current.getLayer('drawing-polygon-points')) {
      map.current.removeLayer('drawing-polygon-points');
    }
    if (map.current.getSource('drawing-polygon')) {
      map.current.removeSource('drawing-polygon');
    }
    if (map.current.getSource('drawing-polygon-points')) {
      map.current.removeSource('drawing-polygon-points');
    }

    if (isDrawingZone && drawnPolygon && drawnPolygon.length > 0) {
      const isComplete = drawnPolygon.length > 2 &&
        drawnPolygon[0][0] === drawnPolygon[drawnPolygon.length - 1][0] &&
        drawnPolygon[0][1] === drawnPolygon[drawnPolygon.length - 1][1];

      // Show fill for 3+ points (even if not closed yet)
      const canShowFill = drawnPolygon.length >= 3;

      // For rendering an unclosed polygon as filled, we need to temporarily close it
      let polygonCoords = drawnPolygon;
      if (canShowFill && !isComplete) {
        polygonCoords = [...drawnPolygon, drawnPolygon[0]];
      }

      map.current.addSource('drawing-polygon', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: canShowFill ? 'Polygon' : 'LineString',
            coordinates: canShowFill ? [polygonCoords] : drawnPolygon
          }
        }
      });

      if (canShowFill) {
        map.current.addLayer({
          id: 'drawing-polygon-fill',
          type: 'fill',
          source: 'drawing-polygon',
          paint: {
            'fill-color': '#3b82f6',
            'fill-opacity': 0.3
          }
        });
      }

      map.current.addLayer({
        id: 'drawing-polygon-line',
        type: 'line',
        source: 'drawing-polygon',
        paint: {
          'line-color': '#3b82f6',
          'line-width': 3
        }
      });

      map.current.addSource('drawing-polygon-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: drawnPolygon.map(coord => ({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: coord
            }
          }))
        }
      });

      map.current.addLayer({
        id: 'drawing-polygon-points',
        type: 'circle',
        source: 'drawing-polygon-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#3b82f6',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      });
    }
  }, [isDrawingZone, drawnPolygon, mapLoaded]);

  // Render zones on the map
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clean up existing zone layers
    if (map.current.getLayer('zones-fill')) {
      map.current.removeLayer('zones-fill');
    }
    if (map.current.getLayer('zones-line')) {
      map.current.removeLayer('zones-line');
    }
    if (map.current.getSource('zones')) {
      map.current.removeSource('zones');
    }

    if (zones.length > 0) {
      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

      const zonesToRender = zones;

      const features = zonesToRender
        .filter(zone => zone.shape_type === 'polygon' && zone.coordinates)
        .map((zone, index) => {
          // coordinates is stored as jsonb, convert to proper format
          const coords = typeof zone.coordinates === 'string'
            ? JSON.parse(zone.coordinates)
            : zone.coordinates;

          const zoneIndex = zones.findIndex(z => z.id === zone.id);
          const color = zone.color_override || colors[zoneIndex % colors.length];

          return {
            type: 'Feature' as const,
            properties: {
              id: zone.id,
              name: zone.name,
              color: color
            },
            geometry: {
              type: 'Polygon' as const,
              coordinates: [coords]
            }
          };
        });

      if (features.length > 0) {
        map.current.addSource('zones', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });

        map.current.addLayer({
          id: 'zones-fill',
          type: 'fill',
          source: 'zones',
          paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': 0.3
          }
        });

        map.current.addLayer({
          id: 'zones-line',
          type: 'line',
          source: 'zones',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 2
          }
        });

        // Add hover effect
        map.current.on('mouseenter', 'zones-fill', () => {
          if (map.current && !isDrawingZoneRef.current) {
            map.current.getCanvas().style.cursor = 'pointer';
          }
        });

        map.current.on('mouseleave', 'zones-fill', () => {
          if (map.current && !isDrawingZoneRef.current) {
            map.current.getCanvas().style.cursor = '';
          }
        });

        // Add click handler for zones
        map.current.on('click', 'zones-fill', (e) => {
          if (isDrawingZoneRef.current) {
            return;
          }
          if (e.features && e.features.length > 0) {
            const feature = e.features[0];
            const zoneId = feature.properties?.id;
            const zone = zones.find(z => z.id === zoneId);
            if (zone && onZoneClick) {
              e.originalEvent.stopPropagation();
              onZoneClick({ ...zone, _clickedFrom: 'fill' });
            }
          }
        });
      }
    }
  }, [zones, mapLoaded, onZoneClick]);

  // Update zone markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    zoneMarkers.current.forEach(marker => marker.remove());
    zoneMarkers.current = [];

    const zoneGroups = new globalThis.Map<string, Zone[]>();
    const GROUPING_THRESHOLD = 0.001;

    zones.forEach(zone => {
      let foundGroup = false;

      for (const [key, group] of zoneGroups.entries()) {
        const [lat, lng] = key.split(',').map(Number);
        const distance = Math.sqrt(
          Math.pow(zone.latitude - lat, 2) +
          Math.pow(zone.longitude - lng, 2)
        );

        if (distance < GROUPING_THRESHOLD) {
          group.push(zone);
          foundGroup = true;
          break;
        }
      }

      if (!foundGroup) {
        const key = `${zone.latitude},${zone.longitude}`;
        zoneGroups.set(key, [zone]);
      }
    });

    zoneGroups.forEach((groupZones) => {
      const firstZone = groupZones[0];
      const count = groupZones.length;

      const el = document.createElement('div');
      el.className = 'zone-marker';
      el.style.width = '40px';
      el.style.height = '40px';
      el.style.cursor = 'pointer';
      el.style.position = 'relative';

      const countBadge = count > 1 ? `
        <div style="position: absolute; top: -4px; right: -4px; width: 20px; height: 20px; background-color: #ef4444; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.2); border: 2px solid white;">
          ${count}
        </div>
      ` : '';

      el.innerHTML = `
        <div style="position: relative; width: 40px; height: 40px; border-radius: 50%; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; transition: transform 0.2s; background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%); border: 3px solid white; font-weight: bold; color: white; font-size: 18px;">
          Z
          ${countBadge}
        </div>
      `;

      el.addEventListener('mouseenter', () => {
        if (isDrawingZoneRef.current) return;
        const inner = el.querySelector('div') as HTMLElement;
        if (inner) inner.style.transform = 'scale(1.1)';
      });

      el.addEventListener('mouseleave', () => {
        if (isDrawingZoneRef.current) return;
        const inner = el.querySelector('div') as HTMLElement;
        if (inner) inner.style.transform = 'scale(1)';
      });

      el.addEventListener('click', (e) => {
        if (isDrawingZoneRef.current) {
          return;
        }
        e.stopPropagation();
        if (onZoneClick) {
          const zoneWithGroupInfo = {
            ...firstZone,
            _groupZones: groupZones,
            _groupCount: count,
            _clickedFrom: 'marker'
          };
          onZoneClick(zoneWithGroupInfo);
        }
      });

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([firstZone.longitude, firstZone.latitude])
        .addTo(map.current);

      zoneMarkers.current.push(marker);
    });
  }, [zones, mapLoaded, onZoneClick]);

  // Update markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Group pins by location (use globalThis.Map to avoid shadowing)
    const pinGroups = new globalThis.Map<string, AudioPin[]>();
    audioPins.forEach(pin => {
      const key = `${pin.latitude.toFixed(4)},${pin.longitude.toFixed(4)}`;
      if (!pinGroups.has(key)) {
        pinGroups.set(key, []);
      }
      pinGroups.get(key)!.push(pin);
    });

    // Create markers
    pinGroups.forEach((pinsAtLocation) => {
      const pin = pinsAtLocation[0];
      const count = pinsAtLocation.length;

      // Create marker element
      const el = document.createElement('div');
      el.className = 'custom-marker';
      el.style.width = '40px';
      el.style.height = '40px';
      el.style.cursor = 'pointer';

      const bgColor = (pin as any).color_override || ((pin as any).source === 'ai' ? '#9333ea' : '#3b82f6');

      const countBadge = count > 1 ? `
        <div style="position: absolute; top: -4px; right: -4px; width: 20px; height: 20px; background-color: #ef4444; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
          ${count}
        </div>
      ` : '';

      el.innerHTML = `
        <div style="position: relative; width: 40px; height: 40px; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; transition: opacity 0.2s; background-color: ${bgColor};">
          <svg style="width: 20px; height: 20px; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          ${countBadge}
        </div>
      `;

      el.addEventListener('mouseenter', () => {
        el.style.opacity = '0.8';
      });

      el.addEventListener('mouseleave', () => {
        el.style.opacity = '1';
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onPinClick(pin);

        if (map.current) {
          map.current.flyTo({
            center: [pin.longitude, pin.latitude],
            zoom: 12,
            duration: 1500,
            essential: true
          });
        }
      });

      // Create popup content
      const isOwnPin = currentUserId && pin.user_id === currentUserId;
      const actionButtons = isOwnPin
        ? `<div style="margin-top: 8px; display: flex; gap: 8px;">
             <button
               style="flex: 1; padding: 6px 12px; background-color: #3b82f6; color: white; font-size: 12px; border-radius: 4px; border: none; cursor: pointer; transition: background-color 0.2s;"
               onmouseover="this.style.backgroundColor='#2563eb'"
               onmouseout="this.style.backgroundColor='#3b82f6'"
               onclick="window.dispatchEvent(new CustomEvent('edit-pin', { detail: '${pin.id}' }))"
             >
               Edit
             </button>
             <button
               style="flex: 1; padding: 6px 12px; background-color: #ef4444; color: white; font-size: 12px; border-radius: 4px; border: none; cursor: pointer; transition: background-color 0.2s;"
               onmouseover="this.style.backgroundColor='#dc2626'"
               onmouseout="this.style.backgroundColor='#ef4444'"
               onclick="if(confirm('Are you sure you want to delete this pin?')) window.dispatchEvent(new CustomEvent('delete-pin', { detail: '${pin.id}' }))"
             >
               Delete
             </button>
           </div>`
        : '';

      const escapeHtml = (str: string) => {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      };

      const popupTitle = count > 1
        ? `${count} songs at this location`
        : escapeHtml(pin.title);
      const popupDesc = pin.description ? escapeHtml(pin.description) : '';

      const marker = new mapboxgl.Marker(el)
        .setLngLat([pin.longitude, pin.latitude])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 }).setHTML(`
            <div style="padding: 8px;">
              <h3 style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${popupTitle}</h3>
              ${count === 1 ? `
                <p style="font-size: 12px; color: #6b7280; margin-top: 4px;">${popupDesc}</p>
                <p style="font-size: 12px; color: #9ca3af; margin-top: 4px;">${pin.play_count} plays</p>
                ${actionButtons}
              ` : `
                <p style="font-size: 12px; color: #6b7280; margin-top: 4px;">Click to see all songs</p>
              `}
            </div>
          `)
        )
        .addTo(map.current!);

      markers.current.push(marker);
    });
  }, [audioPins, mapLoaded, currentUserId]);

  // Handle search location marker
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove existing search marker
    if (searchMarker.current) {
      searchMarker.current.remove();
      searchMarker.current = null;
    }

    // Add new search marker if location is provided
    if (searchLocation) {
      const el = document.createElement('div');
      el.className = 'search-marker';
      el.style.width = '48px';
      el.style.height = '48px';
      el.style.cursor = 'pointer';
      el.style.zIndex = '1000';

      // Use blue logo for light map backgrounds
      el.innerHTML = `
        <div style="position: relative; width: 48px; height: 48px; animation: bounce 1s ease-in-out 3;">
          <img
            src="/MapSound Logo_Blue.png"
            alt="Search Location"
            style="width: 100%; height: 100%; filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));"
          />
          <div style="position: absolute; bottom: -24px; left: 50%; transform: translateX(-50%); background-color: white; padding: 4px 8px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.2); white-space: nowrap; font-size: 12px; font-weight: 500; color: #1f2937; max-width: 200px; overflow: hidden; text-overflow: ellipsis;">
            ${searchLocation.name}
          </div>
        </div>
      `;

      // Add bounce animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `;
      document.head.appendChild(style);

      searchMarker.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([searchLocation.lng, searchLocation.lat])
        .addTo(map.current);
    }
  }, [searchLocation, mapLoaded]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-gray-800 border border-gray-700 rounded-lg">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Configuration Required</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="text-left bg-gray-50 p-4 rounded-lg text-sm">
            <p className="font-semibold mb-2">To enable the map:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-300">
              <li>Get a free token from <a href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer" className="text-[#00c7b7] hover:underline">mapbox.com</a></li>
              <li>Add it to your .env file as VITE_MAPBOX_ACCESS_TOKEN</li>
              <li>Restart the development server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={mapContainer} className="w-full h-full" />
  );
}
